import { Box, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { getAssetName } from '@/utils/assets';
import { Ellipsis } from '@/components/Ellipsis/index';
import {
  PercentagePillContainer,
  PercentagePillFill,
  PercentagePillText,
  RenderPercentage,
} from '../Search/SearchTable/cellDefinitions';
import { MatchAsset } from '../Search/SearchTable';
import { UnsortedColumnIcon } from '../DataGrid/UnsortedColumnIcon';
import { ReportBreadcrumbToolbar } from './ReportBreadcrumbToolbar';
import {
  NO_VISIBLE_MATCH_LABEL,
  NO_VISIBLE_MATCH_MARK,
  NO_VISIBLE_MATCH_TOOLTIP,
  getClosestMatch,
  getClosestMatchDescription,
  getMatchCount,
  orderByClosestMatch,
} from './groupAssetMatches';

const ROW_HEIGHT = 80;

const NoVisibleMatch = () => (
  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
    {NO_VISIBLE_MATCH_LABEL}
  </Typography>
);

// An empty cell is the original bug, so the row still gets a pill — but a mark rather than a
// number, because no percentage was measured for this asset within this group. The words stay
// in the Closest to cell.
const NoVisibleMatchPill = () => (
  <Tooltip title={NO_VISIBLE_MATCH_TOOLTIP}>
    <PercentagePillContainer>
      <PercentagePillFill style={{ width: '0%' }} />
      {/* The shared pill draws its label white for contrast against the filled bar. With no bar,
          the label needs a colour that reads against the empty track instead. */}
      <PercentagePillText sx={{ color: 'text.primary' }}>{NO_VISIBLE_MATCH_MARK}</PercentagePillText>
    </PercentagePillContainer>
  </Tooltip>
);

const closestMatchColumn: GridColDef<ReportGroupAsset> = {
  field: 'closestMatch',
  headerName: 'Closest match',
  width: 130,
  minWidth: 110,
  filterable: false,
  sortable: false,
  renderCell: parameters => {
    const closest = getClosestMatch(parameters.row);
    if (!closest) return <NoVisibleMatchPill />;

    const matchAsset: MatchAsset = {
      ...closest.asset,
      contentType: 'asset',
      matchPercentage: closest.matchPercentage,
    };
    return (
      <RenderPercentage
        value={closest.matchPercentage}
        message={getClosestMatchDescription(parameters.row)}
        searchAsset={parameters.row}
        matchAsset={matchAsset}
      />
    );
  },
};

const matchCountColumn: GridColDef<ReportGroupAsset> = {
  field: 'matchCount',
  headerName: 'Matches',
  width: 90,
  minWidth: 80,
  filterable: false,
  sortable: false,
  renderCell: parameters => <Typography variant='body2'>{getMatchCount(parameters.row)}</Typography>,
};

const closestToColumn: GridColDef<ReportGroupAsset> = {
  field: 'closestTo',
  headerName: 'Closest to',
  minWidth: 180,
  flex: 1,
  filterable: false,
  sortable: false,
  renderCell: parameters => {
    const closest = getClosestMatch(parameters.row);
    if (!closest) return <NoVisibleMatch />;

    return <Ellipsis text={getAssetName(closest.asset)} />;
  },
};

export const ReportMasterDetail = ({
  assets,
  groupName,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  toolbarActions,
  onRowContextMenu,
}: {
  assets: ReportGroupAsset[];
  groupName: string;
  columns: GridColDef[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  toolbarActions?: React.ReactNode;
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
}) => {
  const rows = orderByClosestMatch(assets);

  const gridColumns = (() => {
    const withoutTreeMatchColumn = columns.filter(column => column.field !== 'matchPercentage');
    return [
      withoutTreeMatchColumn[0],
      closestMatchColumn,
      matchCountColumn,
      closestToColumn,
      ...withoutTreeMatchColumn.slice(1),
    ] as GridColDef[];
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <ReportBreadcrumbToolbar groupName={groupName} toolbarActions={toolbarActions} />
      <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
        <DataGridPro
          rows={rows}
          columns={gridColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={40}
          density='compact'
          disableRowSelectionOnClick
          disableColumnFilter
          getRowId={row => row.id}
          slots={{ columnUnsortedIcon: UnsortedColumnIcon }}
          slotProps={{
            ...(onRowContextMenu && {
              row: { onContextMenu: onRowContextMenu },
            }),
          }}
          sx={{
            height: '100%',
            border: 'none',
            '& .MuiDataGrid-cell': {
              lineHeight: 'unset !important',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
          }}
        />
      </Box>
    </Box>
  );
};
